import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "super_admin" | "admin" | "editor" | "viewer" | "support_manager";

interface PermissionsState {
  loading: boolean;
  userId: string | null;
  roles: AppRole[];
  permissions: Set<string>;
  isSuperAdmin: boolean;
  can: (perm: string) => boolean;
  refresh: () => Promise<void>;
}

export const usePermissions = (): PermissionsState => {
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData.session?.user.id ?? null;
    setUserId(uid);
    if (!uid) {
      setRoles([]);
      setPermissions(new Set());
      setLoading(false);
      return;
    }
    const { data: roleRows } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid);
    const userRoles = (roleRows?.map((r: any) => r.role) ?? []) as AppRole[];
    setRoles(userRoles);

    if (userRoles.length === 0) {
      setPermissions(new Set());
      setLoading(false);
      return;
    }

    const { data: permRows } = await supabase
      .from("role_permissions" as any)
      .select("permission_key, role")
      .in("role", userRoles as any);
    const perms = new Set<string>((permRows ?? []).map((r: any) => r.permission_key));
    setPermissions(perms);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      load();
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSuperAdmin = roles.includes("super_admin");
  const can = (perm: string) => isSuperAdmin || permissions.has(perm);

  return { loading, userId, roles, permissions, isSuperAdmin, can, refresh: load };
};
